import React, { useState, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { setGlobalSuccessMsg, setGlobalErrorMsg } from 'store/global/globalActions';
import { updateWebsiteNginxConfig } from 'store/website/websiteActions';
import { updateGlobalNginxConfig } from 'store/setting/settingActions';
import { globalNginxConfig } from 'store/setting/settingSelectors';
import { WPSForm } from 'styles/layout/forms';
import { Content } from 'styles/globalStyles';
import { TitleBar, GroupTitle } from 'styles/layout/titlebar';
import WebsiteHelper from 'helpers/website';
import UserHelper from 'helpers/user';
import JsxHelper from 'helpers/jsx';
import { useSelector } from 'react-redux';
import { isEmptyOrNull } from 'helpers';
import { Container } from 'styles/website/profile';

const NginxConfig = ({ website }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const globalConfig = useSelector(globalNginxConfig);
  const isGlobalPage = !website;
  const websiteConfig = website ? WebsiteHelper.getNginxConfig(website) : {};
  const NGINX_CONFIG = WebsiteHelper.getNginxConfigOptions();
  const NGINX_DESC = 'Boost your WordPress website security and performance by configuring Nginx settings. ' 
    + (website
      ? 'These settings will override the global settings.'
      : 'These settings will be applied to all websites by default, but can be overridden on a per-website basis.');
  const TABS_INFO = [
    website ? { title: 'Headers', value: 'headers' } : null,
    { title: 'Access Control', value: 'access' },
    website ? { title: 'Advanced', value: 'advanced' } : null,
    { title: 'Custom', value: 'custom' },
  ].filter(Boolean);
  const hasCustomHeaders = !isEmptyOrNull(websiteConfig.custom_headers);
  const [currentTab, setCurrentTab] = useState(TABS_INFO[0].value);
  const initialUserConfig = website ? {
    x_frame_options: websiteConfig.x_frame_options,
    x_xss_protection: websiteConfig.x_xss_protection,
    content_security_policy: websiteConfig.content_security_policy,
    referrer_policy: websiteConfig.referrer_policy,
    ssl_buffer_size: websiteConfig.ssl_buffer_size,
    fastcgi_buffers: websiteConfig.fastcgi_buffers,
    fastcgi_buffer_size: websiteConfig.fastcgi_buffer_size,
    fastcgi_param_http_auth: websiteConfig.fastcgi_param_http_auth || 'no',
    allow_seo_robots: websiteConfig.allow_seo_robots || 'default',
    rate_limit_good_bots: websiteConfig.rate_limit_good_bots || 'default',
    allow_xmlrpc: websiteConfig.allow_xmlrpc || 'default',
    custom_headers: websiteConfig.custom_headers || '',
    custom_pre: websiteConfig.custom_pre || '',
    custom_post: websiteConfig.custom_post || '',
    trailing_slash: websiteConfig.trailing_slash || 'default',
    proxy_cache_html_expires: websiteConfig.proxy_cache_html_expires || 'default',
    compression_algo: websiteConfig.compression_algo || 'default',
  } : {
    // Note: The allow_* values are boolean.
    allow_seo_robots: globalConfig.allow_seo_robots ? 'yes' : 'no',
    allow_xmlrpc: globalConfig.allow_xmlrpc ? 'yes' : 'no',
    custom_headers: globalConfig.custom_headers || '',
    custom_pre: globalConfig.custom_pre || '',
    custom_post: globalConfig.custom_post || '',
  };

  const BREADCRUMBS = JsxHelper.createBreadcrumbs('Nginx Config', 'settings');

  const initDetails = (config) => {
    for (const key of Object.keys(config)) {
      if (!config[key]) {
        const option = NGINX_CONFIG.find(option => option.name === key);
        if (!option) {
          window.logHelper.error(`Nginx option ${key} not found as a valid option`, config);
          continue;
        } else if (!option.options) {
          continue; // Skip if not a select type.
        }
        config[key] = option.default || (option.options && option.options[0].value) || null;
      }
    }
    return config;
  }
  const [details, setDetails] = useState(initDetails(initialUserConfig));

  let configData = NGINX_CONFIG.filter(item => website ? item : item.global);
  for (const i in configData) {
    // If is not a select type, skip.
    if (!configData[i].options) {
      continue;
    }
    // Initialize the options to include the name of the config.
    const configName = configData[i].name;
    configData[i].options = configData[i].options.map(o => ({...o, name: configName}));
    const userValue = details[configName];
    const selectedOption = configData[i].options.find(o => o.value === userValue)
    configData[i].value = {
      value: selectedOption ? selectedOption.value : configData[i].options[0].value,
      label: selectedOption ? selectedOption.label : configData[i].options[0].label
    }
  }

  // -------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------

  const onChange = e => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => website ? submitWebsiteData() : submitGlobalData();

  // -------------------------------------------------------
  // API CALLS
  // -------------------------------------------------------

  const submitGlobalData = () => {
    const data = {
      ...details,
    };
    setLoading(true);
    dispatch(updateGlobalNginxConfig(data))
      .then(() => dispatch(setGlobalSuccessMsg({ model: 'Nginx configuration', action: 'updated', plural: true })))
      .catch(err => dispatch(setGlobalErrorMsg(err)))
      .finally(() => setLoading(false));
  }

  const submitWebsiteData = () => {
    const data = {
      website_slug: website.slug,
      ...details,
    };
    setLoading(true);
    dispatch(updateWebsiteNginxConfig(data))
      .then(() => dispatch(setGlobalSuccessMsg({ model: 'Nginx configuration', onId: WebsiteHelper.getLabel(website), action: 'updated', plural: true })))
      .catch(err => dispatch(setGlobalErrorMsg(err)))
      .finally(() => setLoading(false));
  }

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  const isInputDisabled = (item) => {
    if (item.category === 'custom') {
      return !UserHelper.isAdminOrAgent();
    }
    return false;
  }

  const renderConfigInput = (item) => {
    const commonProps = {
      label: item.title,
      labelClass: 'margin-bottom-12',
      tooltip: item.desc,
      name: item.name,
      value: details[item.name],
      disabled: isInputDisabled(item),
      onChange,
    };
    return item.type !== 'textarea' ? JsxHelper.createSelectInput({
      ...commonProps,
      selectClass: 'secondary-box-select',
      options: item.options,
      sortOff: true,
    }) : JsxHelper.createTextareaInput({
      ...commonProps,
      placeholder: item.placeholder,
      rows: 10,
      style: { resize: 'auto', minWidth: '800px' },
    });
  }

  const renderTabNotices = () => <Fragment>
    {currentTab === 'headers' && <Fragment>
      {hasCustomHeaders && (JsxHelper.createWarningBox('Custom headers are set. The settings below will be ignored.'))}
      {!UserHelper.isAdminOrAgent() && !hasCustomHeaders && JsxHelper.createTipBox('Want to add custom headers? Please contact support.', false, 'Notice')}
    </Fragment>}
    {currentTab === 'custom' && <Fragment>
      {!UserHelper.isAdminOrAgent() && JsxHelper.createTipBox('Want to modify custom Nginx config? Please contact support.', false, 'Notice')}
    </Fragment>}
  </Fragment>;

  return (
    <Container className={isGlobalPage ? 'global-settings margin-0' : 'margin-24'}>
      <TitleBar className={isGlobalPage ? '' : 'titlebar padding-0'}>
        <TitleBar.Title breadcrumbs={isGlobalPage ? BREADCRUMBS : null}>Nginx Config</TitleBar.Title>
        {isGlobalPage && <TitleBar.Actions>{JsxHelper.createBackButton()}</TitleBar.Actions>}
      </TitleBar>
      <p className={`color-primary subheader ${isGlobalPage?'':'padding-left-0'}`}>{NGINX_DESC}</p>
      <Content className={!isGlobalPage?'padding-left-0':''}>
        <GroupTitle>
          <GroupTitle.Filters>
            {TABS_INFO.filter(tab => !tab.disabled).map(tab => (
              <button
                key={tab.value}
                className={currentTab === tab.value ? 'active' : ''}
                onClick={() => setCurrentTab(tab.value)}>
                {tab.title}
              </button>
            ))}
          </GroupTitle.Filters>
        </GroupTitle>
        <div style={{ maxWidth: '550px', marginTop: '10px' }}>
          {renderTabNotices()}
          {configData.filter(item => !item.hide && item.category === currentTab).map((item, index) => 
            <WPSForm.RowItem key={index} margin='0px'>
              {renderConfigInput(item)}
            </WPSForm.RowItem>
          )}
        </div>
        <div className='action-buttons display-flex-nowrap margin-bottom-30' style={{display: 'flex'}}>
          {JsxHelper.createButton({ label: 'Save', loading, onClick: onSubmit, })}
        </div>
      </Content>
    </Container>
  );
};

export default NginxConfig;
